import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import {
    RTB_CAMPAIGN_LIST_ENDPOINT,
    RTB_LIST_DEFAULTS,
    RTB_LIST_PER_PAGE_OPTIONS,
    RTB_LIST_SORT_OPTIONS,
} from '../constants/rtbCampaigns';
import { confirmAction } from './useConfirm';
import { useToast } from './useToast';
import { debounce } from '../utils/debounce';
import { saveBlobResponse, withParsedBlobError } from '../utils/download';
import { notifyHttpError } from '../utils/httpErrors';

const SEARCH_DELAY_MS = 350;

/** Поле фильтра → параметр адресной строки и API. */
const QUERY_KEYS = Object.freeze({
    search: 'search',
    status: 'status',
    advertiserId: 'advertiser_id',
    sort: 'sort',
    page: 'page',
    perPage: 'per_page',
});

const STATUS_FIELDS = ['user_status', 'system_status', 'display_status', 'display_status_label', 'abilities'];

const emptyMeta = () => ({ current_page: 1, last_page: 1, per_page: RTB_LIST_DEFAULTS.perPage, total: 0 });

const readFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filters = { ...RTB_LIST_DEFAULTS };
    Object.entries(QUERY_KEYS).forEach(([field, key]) => {
        if (params.has(key)) filters[field] = params.get(key);
    });

    filters.page = Math.max(1, Number.parseInt(filters.page, 10) || 1);
    filters.perPage = RTB_LIST_PER_PAGE_OPTIONS.includes(Number(filters.perPage)) ? Number(filters.perPage) : RTB_LIST_DEFAULTS.perPage;
    if (!RTB_LIST_SORT_OPTIONS.some(option => option.value === filters.sort)) filters.sort = RTB_LIST_DEFAULTS.sort;

    return filters;
};

const writeFiltersToUrl = (filters) => {
    const params = new URLSearchParams();
    Object.entries(QUERY_KEYS).forEach(([field, key]) => {
        const value = field === 'search' ? filters.search.trim() : filters[field];
        if (String(value) !== String(RTB_LIST_DEFAULTS[field])) params.set(key, value);
    });
    const query = params.toString();
    window.history.replaceState(window.history.state, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
};

const apiParams = (filters) => {
    const [sort, direction] = filters.sort.split(':');
    return Object.fromEntries(Object.entries({
        search: filters.search.trim(),
        status: filters.status,
        advertiser_id: filters.advertiserId,
        sort,
        direction,
        page: filters.page,
        per_page: filters.perPage,
    }).filter(([, value]) => value !== '' && value != null));
};

/**
 * Серверный список RTB-кампаний одного типа: фильтры синхронизированы с адресной строкой (переживают
 * обновление страницы), поиск — с задержкой, устаревшие ответы отбрасываются, действие над строкой
 * блокирует её до ответа (защита от двойного клика).
 */
export const useRtbCampaignList = (type) => {
    const toast = useToast();
    const filters = reactive(readFiltersFromUrl());
    const rows = ref([]);
    const meta = ref(emptyMeta());
    const statuses = ref([]);
    const isLoading = ref(false);
    const hasLoaded = ref(false);
    const hasLoadError = ref(false);
    const pendingRowIds = reactive(new Set());
    let requestSeq = 0;

    const hasActiveFilters = computed(() => (
        filters.search.trim() !== RTB_LIST_DEFAULTS.search
        || filters.status !== RTB_LIST_DEFAULTS.status
        || String(filters.advertiserId) !== String(RTB_LIST_DEFAULTS.advertiserId)
    ));
    const totalCount = computed(() => statuses.value.reduce((sum, status) => sum + status.count, 0));

    /** silent — фоновое обновление после действия: без спиннера и без экрана ошибки. */
    const load = async ({ silent = false } = {}) => {
        const seq = ++requestSeq;
        if (!silent) {
            isLoading.value = true;
            hasLoadError.value = false;
        }
        try {
            const { data } = await axios.get(RTB_CAMPAIGN_LIST_ENDPOINT[type], { params: apiParams(filters) });
            if (seq !== requestSeq) return;

            const lastPage = data.meta?.last_page || 1;
            if (!data.data?.length && filters.page > lastPage) {
                filters.page = lastPage;
                return;
            }
            rows.value = data.data || [];
            meta.value = data.meta || emptyMeta();
            statuses.value = data.statuses || [];
            hasLoaded.value = true;
        } catch (error) {
            if (seq !== requestSeq) return;
            if (!silent) hasLoadError.value = true;
            notifyHttpError(error, 'Не удалось загрузить кампании');
        } finally {
            if (seq === requestSeq) isLoading.value = false;
        }
    };

    const reload = () => {
        writeFiltersToUrl(filters);
        load();
    };

    const reloadFromFirstPage = () => {
        if (filters.page !== 1) filters.page = 1;
        else reload();
    };

    const debouncedSearch = debounce(reloadFromFirstPage, SEARCH_DELAY_MS);

    watch(() => filters.search, debouncedSearch);
    watch(() => [filters.status, filters.advertiserId, filters.sort, filters.perPage], () => {
        debouncedSearch.cancel();
        reloadFromFirstPage();
    });
    watch(() => filters.page, reload);

    const resetFilters = () => Object.assign(filters, {
        search: RTB_LIST_DEFAULTS.search,
        status: RTB_LIST_DEFAULTS.status,
        advertiserId: RTB_LIST_DEFAULTS.advertiserId,
    });

    const isRowPending = row => pendingRowIds.has(row.id);

    const withRowLock = async (row, action) => {
        if (pendingRowIds.has(row.id)) return;
        pendingRowIds.add(row.id);
        try {
            await action();
        } finally {
            pendingRowIds.delete(row.id);
        }
    };

    const toggleStatus = row => withRowLock(row, async () => {
        try {
            const { data } = await axios.post(row.urls.toggle_status);
            STATUS_FIELDS.forEach((field) => {
                if (field in data) row[field] = data[field];
            });
            toast.success(data.message);
            load({ silent: true });
        } catch (error) {
            notifyHttpError(error, 'Не удалось изменить статус кампании');
        }
    });

    const deleteCampaign = row => withRowLock(row, async () => {
        const isConfirmed = await confirmAction({
            title: 'Удалить кампанию?',
            message: `«${row.name}» исчезнет из списка и перестанет показываться. Статистика и расход сохранятся.`,
            confirmLabel: 'Удалить',
            tone: 'danger',
        });
        if (!isConfirmed) return;

        try {
            const { data } = await axios.delete(row.urls.destroy);
            toast.success(data.message || 'Кампания удалена');
            await load({ silent: true });
        } catch (error) {
            notifyHttpError(error, 'Не удалось удалить кампанию');
        }
    });

    const exportScreens = row => withRowLock(row, async () => {
        try {
            const response = await axios.get(row.urls.export_screens, { responseType: 'blob' });
            saveBlobResponse(response, `campaign-${row.id}-screens.xlsx`);
        } catch (error) {
            notifyHttpError(await withParsedBlobError(error), 'Не удалось скачать список экранов');
        }
    });

    return {
        filters,
        rows,
        meta,
        statuses,
        totalCount,
        isLoading,
        hasLoaded,
        hasLoadError,
        hasActiveFilters,
        load,
        resetFilters,
        isRowPending,
        toggleStatus,
        deleteCampaign,
        exportScreens,
        dispose: debouncedSearch.cancel,
    };
};
